<template>
  <div>
    <div v-if="statusBarVisible" class="flex flex-wrap align-baseline">
      <div class="mr-5 mb-5 medium-text"> {{ selectedAdsCount || 0 }} Selected Ads</div>
      <div id="ads-remove-selected-ads-button" class="speakol speakol-cancel cursor-pointer mt-1 transform" @click="handleClearClicked"></div>
      <div class="vl lg:mr-8 invisible lg:ml-8 lg:visible"></div>
      <div class="flex w-full lg:w-1/2 mb-8">
        <div id="ads-run-all-button" class="flex cursor-pointer" @click="handleRunAllClicked">
          <vs-icon class="mr-5" size="20px" icon="speakol-play-alt" color="#0092D7" icon-pack="speakol" round></vs-icon>
          <div class="mr-8 whitespace-no-wrap play-text">Run All</div>
        </div>
        <div id="ads-pause-all-button" class="flex cursor-pointer" @click="handlePauseAllClicked">
          <vs-icon class="mr-5" size="20px" icon="speakol-pause" color="#DB2828" icon-pack="speakol" round></vs-icon>
          <div class="whitespace-no-wrap pause-text">Pause All</div>
        </div>
      </div>
    </div>

    <a-g-speakol-table
      ref="adsTable"
      :filters="agFilters"
      :columns="agColumns"
      :options="agOptions"
      @selectionChanged="handleSelectionChange"
      @paginationChanged="handlePaginationChange"
      @sortChanged="$emit('sortChanged', $event)"
      @dataChange="$emit('dataChange', $event)"
      @visibleColumnsChanged="$emit('visibleColumnsChanged', $event)"
    />

  </div>
</template>


<script>


import {get, forIn, map, transform, groupBy} from 'lodash'
import AGSpeakolTable from '@/components/AGSpeakolTable'
import AdsDataSource from '@/datasources/AdsDataSource'
import CheckboxHeader from '@/components/AGSpeakolTable/AgCheckboxHeader'
import AdsTitleColumn from './components/adsTitle-col.vue'
import ActionColumn from './components/action-col.vue'
import StatusColumn from './components/status-col.vue'
import ToggleStatusColumn from './components/toggleStatus-col.vue'
import CampaignTitleColumn from './components/campaign-col.vue'
import AccountColumn from './components/account-col.vue'
import AdsetColumn from './components/adset-col.vue'

export default {

  components: {
    AGSpeakolTable,
    AdsTitleColumn,
    ActionColumn,
    StatusColumn,
    ToggleStatusColumn,
    CheckboxHeader,
    CampaignTitleColumn,
    AccountColumn,
    AdsetColumn
  },

  computed: {
    statusBarVisible () {
      return this.selectedAdsCount !== 0
    }
  },

  data () {
    const self = this
    return {
      selectedAdsCount: 0,
      selectedAds: [],
      agFilters: {
        searchQuery: null,
        accountsIds: null,
        campaignsIds: null,
        event: null,
        status: null,
        adsetsIds: null,
        date: {
          startDate: self.calculateStartDate(),
          endDate: this.$moment()
        }
      },
      agOptions: {
        dataSource: AdsDataSource,
        rowSelection: 'multiple',
        maxBlocksInCache: 1,
        rowHeight: 112
      },
      agColumns: this.generateTableColumns()
    }
  },

  methods: {

    calculateStartDate () {
      const accountCreatedAt = new this.$moment(this.$store.getters['authentication/activeAccount'].created_at)
      return accountCreatedAt.format('YYYY-MM-DD')
    },

    handlePaginationChange () {
      this.handleSelectionChange({})
    },

    handleSelectionChange (selectedNodes) {
      this.selectedAdsCount = selectedNodes.length || 0
      this.selectedAds = selectedNodes || []
    },

    handleClearClicked () {
      this.$refs['adsTable'].clearSelection()
      this.selectedAdsCount = 0
      this.selectedAds = []
    },

    async handleRunAllClicked () {
      const adsGroupedByCampaignId = this.groupAdsByCampaignId(this.selectedAds)
      let runSelectedAds = []
      forIn(adsGroupedByCampaignId, (adsIds, campaignId) => {
        runSelectedAds = map(adsIds, (adId) => {
          return this.toggleAds({
            campaignId,
            adId,
            activationStatus: 'Active'
          })
        })
      })
      await Promise.all(runSelectedAds)
      this.$refs['adsTable'].refreshGrid()
    },

    async handlePauseAllClicked () {
      const adsGroupedByCampaignId = this.groupAdsByCampaignId(this.selectedAds)
      let runPauseAds = []
      forIn(adsGroupedByCampaignId, (adsIds, campaignId) => {
        runPauseAds = map(adsIds, (adId) => {
          return this.toggleAds({
            campaignId,
            adId,
            activationStatus: 'Paused'
          })
        })
      })
      await Promise.all(runPauseAds)
      this.$refs['adsTable'].refreshGrid()
    },


    groupAdsByCampaignId (ads = []) {
      return transform(groupBy(ads, 'campaignId'), (result, value, key) => {
        result[key] = map(value, 'id')
        return result
      }, {})
    },


    async toggleAds ({
      campaignId,
      adId,
      activationStatus = 'Active'
    }) {
      await window.axios.post(`/campaigns/${campaignId}/update-ad-activation-status`, {
        status: activationStatus,
        ad_id: adId,
        _method: 'PATCH'
      })

    },


    filter (filters) {
      this.agFilters['searchQuery'] = filters['searchQuery']
      this.agFilters['accountsIds'] = filters['accountsIds']
      this.agFilters['campaignsIds'] = filters['campaignsIds']
      this.agFilters['event'] = filters['event']
      this.agFilters['status'] = filters['status']
      this.agFilters['adsetsIds'] = filters['adsetsIds']
      this.agFilters['date'] = filters['date']
      this.$refs['adsTable'].applyFilters()

      const eventColumnHeader = get(filters, 'event.id', 'conversions') === 'conversions' ? 'Conversions' : filters.event.text

      this.agColumns = this.generateTableColumns({
        eventColumnHeader
      })
      this.$refs['adsTable'].setColumnDefs([])
      this.$refs['adsTable'].setColumnDefs(this.agColumns)
    },

    generateTableColumns (options = {}) {

      const self = this
      const {eventColumnHeader = 'Conversions'} = options

      const advertisingChannelColumns = [
        {
          headerName: 'Advertising Channel',
          field: 'advertising_channel',
          minWidth: 210,
          valueGetter (params) {
            if (params.data) {
              return params.data.advertising_channel_title ? params.data.advertising_channel_title : 'N/A'
            }
          }
        }
      ]

      const columns =  [
        {
          headerName: '',
          minWidth: 75,
          resizable: false,
          headerCheckboxSelection: true,
          headerCheckboxSelectionFilteredOnly: false,
          checkboxSelection: true,
          colId: 'headerCheckbox',
          headerComponentFramework: 'CheckboxHeader'
        },
        {
          headerName: '',
          minWidth: 80,
          maxWidth: 80,
          cellRendererFramework: 'ToggleStatusColumn'
          // headerComponentFramework: 'CheckboxHeader'
        },
        {
          headerName: 'Ad Title',
          colId: 'adTitle',
          minWidth: 265,
          sortable: true,
          cellRendererFramework: 'AdsTitleColumn'

        },
        {
          headerName: 'Account Name',
          colId: 'accountName',
          minWidth: 190,
          sortable: true,
          cellRendererFramework: 'AccountColumn'
        },
        {
          headerName: 'Status',
          colId: 'aggregated_status.status',
          minWidth: 140,
          sortable: true,
          cellRendererFramework: 'StatusColumn'
        },
        ...(this.$hasFeature(this.$constants.FEATURE_SHORTNAME.FACEBOOK_ADVERTISING_CHANNEL) ? advertisingChannelColumns : []),
        {
          headerName: 'Campaign',
          minWidth: 360,
          sortable: true,
          field: 'campaignTitle',
          cellRendererFramework: 'CampaignTitleColumn'
        },
        {
          headerName: 'Ad Set',
          colId: 'adsetTitle',
          minWidth: 120,
          sortable: true,
          cellRendererFramework: 'AdsetColumn'

        },
        {
          headerName: 'Impressions',
          field: 'impressions',
          minWidth: 160,
          sortable: true,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.impressions', 0))
          }
        },
        {
          headerName: 'Clicks',
          field: 'clicks',
          sortable: true,
          minWidth: 110,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.clicks'))
          }
        },
        {
          headerName: 'CTR',
          field: 'ctr',
          sortable: true,
          minWidth: 95,
          sign: '%',
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.ctr', 0), '%')
          }

        },
        {
          headerName: 'Avg. CPC',
          field: 'average_cpc',
          sortable: true,
          minWidth: 135,
          sign: '$',
          valueGetter (params) {
            if (params.data) {
              return self.$options.filters.addComma(get(params, 'data.analytics.average_cpc', 0), '$')
            }
          }
        },
        {
          headerName: eventColumnHeader,
          field: "filter_event",
          minWidth: 200,
          sortable: true,
          valueGetter (params) {
            return self.$options.filters.addComma(self.getSelectedActionStatistics(get(params, 'data.analytics', {})))
          }
        },
        {
          headerName: 'CPA',
          minWidth: 105,
          field: 'cpa',
          sortable: true,
          sign: '$',
          valueGetter (params) {
            return self.$options.filters.addComma(self.getSelectedActionCPA(params.data), '$')
          }
        },
        {
          headerName: 'Spent',
          minWidth: 130,
          field: 'spent',
          sortable: true,
          sign: '$',
          resizable: true,
          valueGetter (params) {
            return `${self.$options.filters.addComma(get(params, 'data.analytics.spent', 0), '$')}`
          }
        },
        // hidden columns
        {
          headerName: 'Video Views',
          field: 'video_views',
          minWidth: 155,
          sortable: true,
          hide: true,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.video_views'))
          }
        },
        {
          headerName: 'Video Start',
          field: 'video_starts',
          minWidth: 155,
          sortable: true,
          hide: true,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.video_starts'))
          }
        },
        {
          headerName: 'Video First Quarters',
          minWidth: 220,
          sortable: true,
          field: 'video_first_quarters',
          hide: true,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.video_first_quarters'))
          }
        },
        {
          headerName: 'Video midpoints',
          field: 'video_midpoints',
          minWidth: 190,
          sortable: true,
          hide: true,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.video_midpoints'))
          }
        },
        {
          headerName: 'Video Third Quarters',
          sortable: true,
          field: 'video_third_quarters',
          minWidth: 220,
          hide: true,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.video_third_quarters'))
          }
        },
        {
          headerName: 'Video Completes',
          minWidth: 190,
          sortable: true,
          field: 'video_completes',
          hide: true,
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.video_completes'))
          }
        },
        {
          headerName: 'Video Repeats',
          minWidth: 190,
          sortable: true,
          hide: true,
          field: 'video_repeats',
          valueGetter (params) {
            return self.$options.filters.addComma(get(params, 'data.analytics.video_repeats'))
          }
        }
      ]

      if (this.$canAny('edit_personal_campaigns', 'edit_all_campaigns')) {
        columns.push({
          headerName: 'Action',
          minWidth: 110,
          cellRendererFramework: 'ActionColumn',
          resizable: false
        })
      }

      return columns
    },

    getSelectedActionStatistics (row = {}) {
      const actionId = get(this, 'agFilters.event.id', 'conversions')
      return actionId === 'conversions' ? get(row, 'conversions', 0) : get(row, actionId, 0)
    },

    getSelectedActionCPA (row = {}) {
      const actionStatistics = get(row, 'filter_cpa', 0)
      return Math.round(actionStatistics * 100) / 100
    }

  }
}


</script>


<style lang="scss" scoped>
/deep/ .total {
  font-weight: 500 !important;
  font-size: 14px !important;
}

.medium-text {
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: 0.1px;
  color: #2c2c2c;
}

.vl {
  height: 25px;
  border-left: 1px solid #000;
}

.play-text {
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: #0092d7;
}

.pause-text {
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: #db2828;
}

/deep/ .ag-theme-alpine {
  height: 100vh !important;
  // min-height:10vh !important;
  // max-height:100vh !important;
  // overflow-y:visible !important
}
</style>
